import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Ticket,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  TrendingUp,
  PieChart,
  Users,
  MapPin,
  Calendar as CalendarIcon,
} from "lucide-react";
import { toast } from "react-toastify";
import eventService from "../../../../api/EventService";

const ConferenceAnalytics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { event_slug } = useParams();
  const [eventData, setEventData] = useState(location.state?.eventData || null);
  const [isLoading, setIsLoading] = useState(!location.state?.eventData);
  const [error, setError] = useState(null);

  const [analytics, setAnalytics] = useState({
    totalSales: 11850,
    ticketSalesData: [
      { day: "Mon", sales: 1200 },
      { day: "Tue", sales: 1900 },
      { day: "Wed", sales: 1500 },
      { day: "Thurs", sales: 1800 },
      { day: "Fri", sales: 2100 },
      { day: "Sat", sales: 1950 },
      { day: "Sun", sales: 1400 },
    ],
    attendeeTurnout: {
      scanned: 75,
      unscanned: 25,
      total: 500,
    },
    ticketTypeDistribution: {
      vip: 225,
      regular: 150,
      premium: 125,
      total: 500,
    },
    attendeeDemographics: {
      ageGroups: [
        { group: "18-25", count: 120, percentage: 24 },
        { group: "26-35", count: 180, percentage: 36 },
        { group: "36-45", count: 120, percentage: 24 },
        { group: "46-55", count: 60, percentage: 12 },
        { group: "55+", count: 20, percentage: 4 },
      ],
      gender: [
        { type: "Male", count: 280, percentage: 56 },
        { type: "Female", count: 200, percentage: 40 },
        { type: "Other", count: 20, percentage: 4 },
      ],
      location: [
        { city: "Accra", count: 250, percentage: 50 },
        { city: "Kumasi", count: 120, percentage: 24 },
        { city: "Tamale", count: 80, percentage: 16 },
        { city: "Other", count: 50, percentage: 10 },
      ],
    },
  });

  useEffect(() => {
    if (eventData) {
      const ticketTypes = eventData.ticket_types || [];
      const totalSlots = eventData.overall_slots ? parseInt(eventData.overall_slots) : 500;
      
      // Generate dummy ticket sales data
      const dummySales = [
        { day: "Mon", sales: 1200 },
        { day: "Tue", sales: 1900 },
        { day: "Wed", sales: 1500 },
        { day: "Thurs", sales: 1800 },
        { day: "Fri", sales: 2100 },
        { day: "Sat", sales: 1950 },
        { day: "Sun", sales: 1400 },
      ];
      const totalSales = dummySales.reduce((sum, d) => sum + d.sales, 0);
      
      // Generate dummy ticket type distribution
      const ticketTypeDistribution = ticketTypes.length > 0
        ? ticketTypes.reduce((acc, ticket, idx) => {
            const percentages = [45, 30, 25]; // VIP, Regular, Premium percentages
            const sold = Math.round((totalSlots * percentages[idx % 3]) / 100);
            acc[`type${idx}`] = sold;
            acc.total = (acc.total || 0) + sold;
            return acc;
          }, { total: 0 })
        : {
            vip: Math.round(totalSlots * 0.45),
            regular: Math.round(totalSlots * 0.30),
            premium: Math.round(totalSlots * 0.25),
            total: totalSlots,
          };
      
      // Generate dummy attendee demographics
      const attendeeDemographics = {
        ageGroups: [
          { group: "18-25", count: Math.round(totalSlots * 0.24), percentage: 24 },
          { group: "26-35", count: Math.round(totalSlots * 0.36), percentage: 36 },
          { group: "36-45", count: Math.round(totalSlots * 0.24), percentage: 24 },
          { group: "46-55", count: Math.round(totalSlots * 0.12), percentage: 12 },
          { group: "55+", count: Math.round(totalSlots * 0.04), percentage: 4 },
        ],
        gender: [
          { type: "Male", count: Math.round(totalSlots * 0.56), percentage: 56 },
          { type: "Female", count: Math.round(totalSlots * 0.40), percentage: 40 },
          { type: "Other", count: Math.round(totalSlots * 0.04), percentage: 4 },
        ],
        location: [
          { city: "Accra", count: Math.round(totalSlots * 0.50), percentage: 50 },
          { city: "Kumasi", count: Math.round(totalSlots * 0.24), percentage: 24 },
          { city: "Tamale", count: Math.round(totalSlots * 0.16), percentage: 16 },
          { city: "Other", count: Math.round(totalSlots * 0.10), percentage: 10 },
        ],
      };
      
      setAnalytics({
        totalSales: totalSales,
        ticketSalesData: dummySales,
        attendeeTurnout: {
          scanned: 75,
          unscanned: 25,
          total: totalSlots,
        },
        ticketTypeDistribution: ticketTypeDistribution,
        attendeeDemographics: attendeeDemographics,
      });
    } else {
      // Default dummy data when eventData is not available
      setAnalytics({
        totalSales: 11850,
        ticketSalesData: [
          { day: "Mon", sales: 1200 },
          { day: "Tue", sales: 1900 },
          { day: "Wed", sales: 1500 },
          { day: "Thurs", sales: 1800 },
          { day: "Fri", sales: 2100 },
          { day: "Sat", sales: 1950 },
          { day: "Sun", sales: 1400 },
        ],
        attendeeTurnout: {
          scanned: 75,
          unscanned: 25,
          total: 500,
        },
        ticketTypeDistribution: {
          vip: 225,
          regular: 150,
          premium: 125,
          total: 500,
        },
        attendeeDemographics: {
          ageGroups: [
            { group: "18-25", count: 120, percentage: 24 },
            { group: "26-35", count: 180, percentage: 36 },
            { group: "36-45", count: 120, percentage: 24 },
            { group: "46-55", count: 60, percentage: 12 },
            { group: "55+", count: 20, percentage: 4 },
          ],
          gender: [
            { type: "Male", count: 280, percentage: 56 },
            { type: "Female", count: 200, percentage: 40 },
            { type: "Other", count: 20, percentage: 4 },
          ],
          location: [
            { city: "Accra", count: 250, percentage: 50 },
            { city: "Kumasi", count: 120, percentage: 24 },
            { city: "Tamale", count: 80, percentage: 16 },
            { city: "Other", count: 50, percentage: 10 },
          ],
        },
      });
    }
  }, [eventData]);

  useEffect(() => {
    const fetchEventData = async () => {
      if (location.state?.eventData) {
        setEventData(location.state.eventData);
        setIsLoading(false);
        return;
      }

      if (!event_slug) {
        setError("Conference identifier is missing");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const eventResponse = await eventService.getEvent(event_slug);

        if (!eventResponse.status || !eventResponse.data) {
          throw new Error(
            eventResponse.message || "Failed to fetch conference"
          );
        }

        let event = null;
        if (eventResponse.data.status_code === 200 && eventResponse.data.data) {
          event = eventResponse.data.data;
        } else if (eventResponse.data && !eventResponse.data.status_code) {
          event = eventResponse.data;
        }

        if (!event) {
          throw new Error("Conference data not found");
        }

        const eventTypes = Array.isArray(event.event_type)
          ? event.event_type
          : [event.event_type];
        if (!eventTypes.includes("conference")) {
          throw new Error("This event is not a conference");
        }

        setEventData(event);
      } catch (err) {
        const errorMessage =
          err.message || "An error occurred while loading conference data";
        setError(errorMessage);
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEventData();
  }, [event_slug, location.state]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [event_slug, location.pathname]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-blue-600 animate-spin" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !eventData) {
    return (
      // Responsiveness
      <div className="px-6 py-4 max-w-screen-lg mx-auto flex flex-col items-center justify-center">
        <button
          onClick={() => navigate("/admin/media/conference")}
          className="flex items-center mb-6 text-gray-600 transition-colors hover:text-gray-900 group"
        >
          <ArrowLeft className="mr-2 w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back to Conferences</span>
        </button>

        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <div className="p-4 mb-4 bg-red-100 rounded-full">
            <AlertCircle className="w-12 h-12 text-red-600" />
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900">
            Error Loading Analytics
          </h2>
          <p className="mb-6 text-gray-600">
            {error || "Conference not found"}
          </p>
          <button
            onClick={() => navigate("/admin/media/conference")}
            className="px-6 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700"
          >
            Go Back to Conferences
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-4 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between w-full">
        <button
          onClick={() =>
            navigate(`/admin/media/conference/view/${event_slug}`, {
              state: { eventData: eventData },
            })
          }
          className="flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 cursor-pointer w-full sm:w-auto self-start"
        >
          <ArrowLeft className="mr-2 w-4 h-4 flex-shrink-0" />
          <span className="truncate">Back to Conference Details</span>
        </button>
        <div className="w-full sm:w-auto">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 break-words">
            {eventData.event_title || eventData.venue_name || "Conference"}{" "}
            Analytics
          </h1>
        </div>
      </div>

      {/* Conference Analytics */}
      <div>
        <h2 className="mb-4 text-xl font-bold text-gray-900">
          Conference Analytics
        </h2>
        {true ? (
          <>
          {/* First Row: Sales and Turnout */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 mb-6">
          {/* Real-time Ticket Sales Tracking */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="mb-1 text-lg font-semibold text-gray-900">
                    Real-time Ticket Sales Tracking
                  </h3>
                  <p className="text-xs text-gray-500">
                    Weekly sales performance
                  </p>
                </div>
                <div className="p-2 bg-orange-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-orange-600" />
                </div>
              </div>
              <div className="flex items-center space-x-6">
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">
                    Total Sales
                  </p>
                  <p className="text-2xl font-bold text-orange-600">
                    ₵{analytics.totalSales.toLocaleString()}
                  </p>
                </div>
                <div className="h-10 w-px bg-gray-200" />
                <div>
                  <p className="text-xs font-medium text-gray-600 mb-1">
                    Average/Day
                  </p>
                  <p className="text-xl font-bold text-orange-700">
                    ₵
                    {Math.round(
                      analytics.totalSales / analytics.ticketSalesData.length
                    ).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
            <TicketSalesChart data={analytics.ticketSalesData} />
          </div>

          {/* Attendee Turnout Distribution */}
          <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="mb-6">
              <h3 className="mb-1 text-lg font-semibold text-gray-900">
                Attendee Turnout Distribution
              </h3>
              <p className="text-xs text-gray-500">Check-in status overview</p>
            </div>
            <div className="flex flex-col items-center space-y-6 lg:flex-row lg:items-start lg:space-y-0 lg:space-x-8">
              {/* Enhanced Donut Chart */}
              <div
                className="relative flex-shrink-0"
                style={{ width: "180px", height: "180px" }}
              >
                <svg width="180" height="180" className="transform -rotate-90">
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#F3F4F6"
                    strokeWidth="16"
                  />
                  {/* Scanned segment */}
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#10B981"
                    strokeWidth="16"
                    strokeDasharray={`${
                      (analytics.attendeeTurnout.scanned / 100) * 439.82
                    } 439.82`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                  {/* Unscanned segment */}
                  <circle
                    cx="90"
                    cy="90"
                    r="70"
                    fill="transparent"
                    stroke="#E5E7EB"
                    strokeWidth="16"
                    strokeDasharray={`${
                      (analytics.attendeeTurnout.unscanned / 100) * 439.82
                    } 439.82`}
                    strokeDashoffset={`-${
                      (analytics.attendeeTurnout.scanned / 100) * 439.82
                    }`}
                    strokeLinecap="round"
                    className="transition-all duration-500"
                  />
                </svg>
                {/* Center content */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <p className="text-xs font-medium text-gray-500 mb-1">
                    Total Attendees
                  </p>
                  <p className="text-3xl font-bold text-gray-900">
                    {analytics.attendeeTurnout.total.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {analytics.attendeeTurnout.scanned}% Checked In
                  </p>
                </div>
              </div>

              {/* Statistics */}
              <div className="flex-1 w-full space-y-4">
                <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-green-500 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Scanned
                        </p>
                        <p className="text-xs text-gray-600">
                          Checked in attendees
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">
                        {analytics.attendeeTurnout.scanned}%
                      </p>
                      <p className="text-xs text-gray-500">
                        {Math.round(
                          (analytics.attendeeTurnout.scanned / 100) *
                            analytics.attendeeTurnout.total
                        ).toLocaleString()}{" "}
                        people
                      </p>
                    </div>
                  </div>
                  <div className="w-full h-2 bg-green-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-500"
                      style={{ width: `${analytics.attendeeTurnout.scanned}%` }}
                    />
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-r from-gray-50 to-slate-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-gray-400 rounded-lg">
                        <XCircle className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-900">
                          Unscanned
                        </p>
                        <p className="text-xs text-gray-600">
                          Not yet checked in
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-600">
                        {analytics.attendeeTurnout.unscanned}%
                      </p>
                      <p className="text-xs text-gray-500">
                        {Math.round(
                          (analytics.attendeeTurnout.unscanned / 100) *
                            analytics.attendeeTurnout.total
                        ).toLocaleString()}{" "}
                        people
                      </p>
                    </div>
                  </div>
                  {/* Progress bar */}
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-gray-400 to-gray-500 rounded-full transition-all duration-500"
                      style={{
                        width: `${analytics.attendeeTurnout.unscanned}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>

          {/* Second Row: Ticket Type Distribution and Attendee Demographics */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Ticket Type Distribution - Only for Conferences */}
            {eventData.ticket_types && eventData.ticket_types.length > 0 && (
              <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h3 className="mb-1 text-lg font-semibold text-gray-900">
                        Ticket Type Distribution
                      </h3>
                      <p className="text-xs text-gray-500">
                        Sales breakdown by ticket type
                      </p>
                    </div>
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <PieChart className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                </div>
                <TicketTypeDistributionChart 
                  ticketTypes={eventData.ticket_types} 
                  distribution={analytics.ticketTypeDistribution}
                />
              </div>
            )}

            {/* Attendee Demographics */}
            <div className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="mb-1 text-lg font-semibold text-gray-900">
                      Attendee Demographics
                    </h3>
                    <p className="text-xs text-gray-500">
                      Age, gender, and location breakdown
                    </p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </div>
              <AttendeeDemographics demographics={analytics.attendeeDemographics} />
            </div>
          </div>
          </>
        ) : (
          <div className="p-12 bg-white rounded-xl border border-gray-200 shadow-sm text-center">
            <div className="p-4 mb-4 bg-blue-100 rounded-full w-20 h-20 mx-auto flex items-center justify-center">
              <Ticket className="w-10 h-10 text-blue-600" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              No Analytics Data Available
            </h3>
            <p className="text-sm text-gray-600">
              Conference analytics will appear here once tickets start selling and attendees begin registering.
            </p>
            {eventData.overall_slots && (
              <p className="mt-4 text-sm font-medium text-gray-700">
                Total Slots Available: {parseInt(eventData.overall_slots).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const TicketSalesChart = ({ data }) => {
  const maxSales = Math.max(...data.map((d) => d.sales), 1);
  const chartHeight = 180;
  const padding = 15;
  const svgWidth = 100;

  const coordinates = data.map((d, i) => {
    const x = padding + (i * (svgWidth - padding * 2)) / (data.length - 1);
    const y =
      chartHeight -
      padding -
      (d.sales / maxSales) * (chartHeight - padding * 2);
    return { x, y };
  });

  const createSmoothPath = (coords) => {
    if (coords.length < 2) return "";
    let path = `M ${coords[0].x}% ${coords[0].y}`;
    for (let i = 0; i < coords.length - 1; i++) {
      const current = coords[i];
      const next = coords[i + 1];
      const prev = coords[i - 1];
      const afterNext = coords[i + 2];
      const cp1x = current.x + (next.x - (prev?.x || current.x)) * 0.3;
      const cp1y = current.y + (next.y - (prev?.y || current.y)) * 0.3;
      const cp2x = next.x - ((afterNext?.x || next.x) - current.x) * 0.3;
      const cp2y = next.y - ((afterNext?.y || next.y) - current.y) * 0.3;
      path += ` C ${cp1x}% ${cp1y}, ${cp2x}% ${cp2y}, ${next.x}% ${next.y}`;
    }
    return path;
  };

  const smoothPath = createSmoothPath(coordinates);
  const areaPath =
    smoothPath +
    ` L ${coordinates[coordinates.length - 1].x}% ${chartHeight - padding}` +
    ` L ${coordinates[0].x}% ${chartHeight - padding} Z`;

  return (
    <div
      className="relative bg-gradient-to-b from-orange-50/20 to-transparent rounded-lg pt-2"
      style={{ height: `${chartHeight + 30}px` }}
    >
      <svg width="100%" height={chartHeight} className="overflow-visible">
        <defs>
          <linearGradient id="salesGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F97316" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#F97316" stopOpacity="0.03" />
          </linearGradient>
          <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#FB923C" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3, 4].map((i) => (
          <line
            key={i}
            x1={`${padding}%`}
            y1={padding + (i * (chartHeight - padding * 2)) / 4}
            x2={`${100 - padding}%`}
            y2={padding + (i * (chartHeight - padding * 2)) / 4}
            stroke="#F3F4F6"
            strokeWidth="1"
            strokeDasharray="3 3"
          />
        ))}
        <path
          d={areaPath}
          fill="url(#salesGradient)"
          className="transition-all duration-500"
        />
        <path
          d={smoothPath}
          fill="none"
          stroke="url(#lineGradient)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="transition-all duration-500"
        />
        {coordinates.map((coord, i) => (
          <circle
            key={i}
            cx={`${coord.x}%`}
            cy={coord.y}
            r="4"
            fill="#F97316"
            stroke="white"
            strokeWidth="2"
            className="transition-all duration-300"
          />
        ))}
        {data.map((d, i) => (
          <text
            key={i}
            x={`${coordinates[i].x}%`}
            y={chartHeight + 12}
            textAnchor="middle"
            className="text-xs font-medium fill-gray-600"
          >
            {d.day}
          </text>
        ))}
      </svg>
      <div
        className="absolute left-0 top-2 flex flex-col justify-between"
        style={{ height: `${chartHeight - padding * 2}px`, width: "30px" }}
      >
        {[0, 1, 2, 3, 4].reverse().map((i) => (
          <span key={i} className="text-xs text-gray-500">
            ₵{((i * maxSales) / 4 / 1000).toFixed(1)}k
          </span>
        ))}
      </div>
    </div>
  );
};

// Ticket Type Distribution Chart Component
const TicketTypeDistributionChart = ({ ticketTypes, distribution }) => {
  const colors = ["#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#EC4899"];
  const total = distribution.total || 1;
  
  // Calculate distribution from ticket types if available
  const distributionData = ticketTypes.map((ticket, idx) => {
    const count = distribution[`type${idx}`] || distribution[ticket.name?.toLowerCase()] || Math.round(total / ticketTypes.length);
    const percentage = (count / total) * 100;
    return {
      name: ticket.name || `Type ${idx + 1}`,
      count,
      percentage: Math.round(percentage),
      color: colors[idx % colors.length],
    };
  });

  const circumference = 2 * Math.PI * 60; // radius = 60
  let currentOffset = 0;

  return (
    <div className="space-y-4">
      {/* Pie Chart - Centered */}
      <div className="flex justify-center">
        <div className="relative flex-shrink-0" style={{ width: "160px", height: "160px" }}>
          <svg width="160" height="160" className="transform -rotate-90">
            <circle
              cx="80"
              cy="80"
              r="60"
              fill="transparent"
              stroke="#F3F4F6"
              strokeWidth="14"
            />
            {distributionData.map((item, idx) => {
              const strokeDasharray = `${(item.percentage / 100) * circumference} ${circumference}`;
              const strokeDashoffset = `-${currentOffset}`;
              currentOffset += (item.percentage / 100) * circumference;
              
              return (
                <circle
                  key={idx}
                  cx="80"
                  cy="80"
                  r="60"
                  fill="transparent"
                  stroke={item.color}
                  strokeWidth="14"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  className="transition-all duration-500"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <p className="text-xs font-medium text-gray-500 mb-1">Total</p>
            <p className="text-2xl font-bold text-gray-900">{total.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="space-y-2">
        {distributionData.map((item, idx) => (
          <div key={idx} className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center space-x-2.5">
              <div
                className="w-3.5 h-3.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: item.color }}
              />
              <div className="min-w-0 flex-1">
                <p className="text-xs font-semibold text-gray-900 truncate">{item.name}</p>
                <p className="text-xs text-gray-600">{item.count.toLocaleString()} tickets</p>
              </div>
            </div>
            <div className="text-right flex-shrink-0 ml-2">
              <p className="text-sm font-bold text-gray-900">{item.percentage}%</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Attendee Demographics Component
const AttendeeDemographics = ({ demographics }) => {
  return (
    <div className="space-y-4">
      {/* Age Groups */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <CalendarIcon className="w-4 h-4 text-blue-600" />
          <h4 className="text-xs font-semibold text-gray-900">Age Groups</h4>
        </div>
        <div className="space-y-1.5">
          {demographics.ageGroups.map((group, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs font-medium text-gray-700">{group.group}</span>
                <span className="text-xs font-semibold text-gray-900">
                  {group.count} ({group.percentage}%)
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                  style={{ width: `${group.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gender Distribution */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <Users className="w-4 h-4 text-purple-600" />
          <h4 className="text-xs font-semibold text-gray-900">Gender</h4>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {demographics.gender.map((item, idx) => (
            <div key={idx} className="p-2 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 text-center">
              <p className="text-xs font-medium text-gray-600 mb-0.5">{item.type}</p>
              <p className="text-lg font-bold text-purple-700">{item.count}</p>
              <p className="text-xs text-gray-600">{item.percentage}%</p>
            </div>
          ))}
        </div>
      </div>

      {/* Location Distribution */}
      <div>
        <div className="flex items-center space-x-2 mb-3">
          <MapPin className="w-4 h-4 text-green-600" />
          <h4 className="text-xs font-semibold text-gray-900">Location</h4>
        </div>
        <div className="space-y-1.5">
          {demographics.location.map((loc, idx) => (
            <div key={idx}>
              <div className="flex items-center justify-between mb-0.5">
                <span className="text-xs font-medium text-gray-700">{loc.city}</span>
                <span className="text-xs font-semibold text-gray-900">
                  {loc.count} ({loc.percentage}%)
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full transition-all duration-500"
                  style={{ width: `${loc.percentage}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ConferenceAnalytics;
